import { PowerSyncContext } from "@powersync/react"
import { useEffect, useRef } from "react"
import * as Sentry from "@sentry/react"
import { powerSyncDb } from "@/lib/powersync/db"
import { SupabaseConnector } from "@/lib/powersync/connector"
import {
  attachmentQueue,
  subscribeToPermanentAttachmentUploadFailures,
} from "@/lib/powersync/attachments"
import { useAuthContext } from "./auth"
import { useActiveProject } from "@/hooks/useActiveProject"
import { thumbnailQueue } from "@/lib/powersync/photoThumbnails"
import { reconcileGeospatialPhotoAssociations } from "@/lib/powersync/reconcileGeospatialPhotoAssociations"
import { useTrackPublisher } from "@/hooks/useTrackPublisher"
import {
  PowerSyncRuntime,
  type PowerSyncRuntimePhase,
} from "@/lib/powersync/runtime"
import { toast } from "@fieldbook/ui/toaster"
import {
  powerSyncConnectionMethod,
  powerSyncConnectionOptions,
} from "@/lib/powersync/connection"
import { isTransientNetworkError } from "@/lib/powersync/downloadErrors"

function isExpectedOfflineAuthError(error: Error) {
  return !navigator.onLine && error.message.includes("Not authenticated")
}

function captureLifecycleError(error: unknown, phase: PowerSyncRuntimePhase) {
  const captured = error instanceof Error ? error : new Error(String(error))
  if (phase === "connect" && isExpectedOfflineAuthError(captured)) {
    console.info("[PowerSync] Connection deferred while offline")
    return
  }
  console.error(`[PowerSync] Lifecycle ${phase} failed:`, captured)
  Sentry.captureException(captured, {
    tags: {
      source: "powersync-lifecycle",
      phase,
      driver: __POWERSYNC_DRIVER__,
      connection_method: powerSyncConnectionMethod,
    },
  })
}

/**
 * Originals and thumbnails start and stop together, but as separate queues:
 * a thumbnail problem must never hold up an original's upload.
 */
const photoTransfers = {
  async startSync() {
    await attachmentQueue.startSync()
    // Awaited so a stop can't finish before this start does, but a failure
    // only costs thumbnails.
    try {
      await thumbnailQueue.startSync()
    } catch (error) {
      console.error("[Thumbnails] Failed to start:", error)
    }
  },
  async stopSync() {
    await thumbnailQueue.stopSync()
    await attachmentQueue.stopSync()
  },
}

export function PowerSyncProvider({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useAuthContext()
  const [activeProjectId] = useActiveProject()
  const lastReportedDownloadErrorRef = useRef<string | null>(null)
  const readyDiagnosticLoggedRef = useRef(false)
  const providerMountedAtRef = useRef(performance.now())
  const wasConnectedRef = useRef(false)
  const hasConnectedRef = useRef(false)
  const reconnectCountRef = useRef(0)
  const previousProjectIdRef = useRef(activeProjectId)
  const runtimeRef = useRef<PowerSyncRuntime<SupabaseConnector> | null>(null)
  if (!runtimeRef.current) {
    runtimeRef.current = new PowerSyncRuntime(
      powerSyncDb,
      photoTransfers,
      () => new SupabaseConnector(),
      powerSyncConnectionOptions,
      captureLifecycleError,
    )
  }
  const runtime = runtimeRef.current
  // Starting the runtime while offline is safe: its connector only obtains
  // real Supabase credentials, while PowerSync and attachment work queue
  // until connectivity returns. Do not use the synthetic UI session here.
  const canSync = isLoggedIn

  useTrackPublisher(canSync)

  useEffect(
    () =>
      subscribeToPermanentAttachmentUploadFailures(({ status, automatic }) => {
        // The daily automatic retry failing again isn't news to the user.
        if (automatic) return
        // Also listed, with retry, in the project's upload details.
        toast.error(
          status === 413
            ? "A photo is too large to upload. It's kept on this device; tap Retry on the photo to try again. Details are in the project's upload status."
            : "A photo couldn't be uploaded. It's kept on this device and will retry tomorrow, or tap Retry on the photo. Details are in the project's upload status.",
          { duration: 10_000 },
        )
      }),
    [],
  )

  // Report sync download errors to Sentry, except transient network drops.
  // Registered app-wide (not per project) so errors are captured regardless
  // of which screen is open.
  // Deduped per distinct error message: statusChanged fires on every status
  // update and the same error persists until the next successful download.
  useEffect(() => {
    console.info("[PowerSync] runtime configured", {
      driver: __POWERSYNC_DRIVER__,
      connectionMethod: powerSyncConnectionMethod,
    })
    return powerSyncDb.registerListener({
      statusChanged: (status) => {
        if (status.connected && !wasConnectedRef.current) {
          if (hasConnectedRef.current) {
            reconnectCountRef.current += 1
            console.info("[PowerSync] connection resumed", {
              driver: __POWERSYNC_DRIVER__,
              connectionMethod: powerSyncConnectionMethod,
              reconnectCount: reconnectCountRef.current,
            })
            Sentry.addBreadcrumb({
              category: "powersync.connection",
              level: "info",
              message: "PowerSync connection resumed",
              data: {
                driver: __POWERSYNC_DRIVER__,
                connectionMethod: powerSyncConnectionMethod,
                reconnectCount: reconnectCountRef.current,
              },
            })
          }
          hasConnectedRef.current = true
        }
        wasConnectedRef.current = status.connected

        if (status.hasSynced && !readyDiagnosticLoggedRef.current) {
          readyDiagnosticLoggedRef.current = true
          console.info("[PowerSync] local data ready", {
            driver: __POWERSYNC_DRIVER__,
            connectionMethod: powerSyncConnectionMethod,
            elapsedMs: Math.round(
              performance.now() - providerMountedAtRef.current,
            ),
          })
        }
        const downloadError = status.downloadError
        if (!downloadError) {
          lastReportedDownloadErrorRef.current = null
          return
        }
        if (isExpectedOfflineAuthError(downloadError)) return
        if (lastReportedDownloadErrorRef.current === downloadError.message)
          return
        lastReportedDownloadErrorRef.current = downloadError.message
        // Dropped connections are expected in the field and PowerSync retries
        // them. Keep them as context for later errors instead of reporting
        // each drop on a flapping connection as its own Sentry event.
        if (isTransientNetworkError(downloadError)) {
          Sentry.addBreadcrumb({
            category: "powersync.connection",
            level: "warning",
            message: `PowerSync download interrupted: ${downloadError.message}`,
            data: {
              connected: status.connected,
              lastSyncedAt: status.lastSyncedAt?.toISOString() ?? null,
            },
          })
          return
        }
        console.error("[PowerSync] Download error:", downloadError)
        Sentry.captureException(downloadError, {
          tags: {
            source: "powersync-download",
            driver: __POWERSYNC_DRIVER__,
            connection_method: powerSyncConnectionMethod,
          },
          extra: {
            connected: status.connected,
            lastSyncedAt: status.lastSyncedAt?.toISOString() ?? null,
          },
        })
      },
    })
  }, [])

  // Connect when isLoggedIn changes to true (fresh login)
  useEffect(() => {
    if (canSync) {
      void runtime.start().catch(() => {
        // The runtime reports lifecycle failures with their phase.
      })
    } else {
      wasConnectedRef.current = false
      hasConnectedRef.current = false
      reconnectCountRef.current = 0
      void runtime.stop().catch(() => {
        // The runtime reports lifecycle failures with their phase.
      })
    }
  }, [canSync, runtime])

  // This is a local SQLite repair and must also run when sync cannot start
  // (for example, when opening the app offline).
  useEffect(() => {
    if (!canSync) return
    let active = true
    void powerSyncDb
      .waitForReady()
      .then(() => {
        if (active) return reconcileGeospatialPhotoAssociations()
      })
      .catch((error) => {
        if (!active) return
        console.error(
          "[PowerSync] Geospatial photo reconciliation failed:",
          error,
        )
        Sentry.captureException(error)
      })
    return () => {
      active = false
    }
  }, [canSync])

  // Restart attachment sync when active project changes so the
  // watchAttachments query picks up the new project_id filter.
  useEffect(() => {
    if (previousProjectIdRef.current === activeProjectId) return
    previousProjectIdRef.current = activeProjectId
    void runtime.restartAttachments().catch(() => {
      // The runtime reports lifecycle failures with their phase.
    })
  }, [activeProjectId, runtime])

  useEffect(() => {
    return () => {
      void runtime.stop().catch(() => {
        // The runtime reports lifecycle failures with their phase.
      })
    }
  }, [runtime])

  return (
    <PowerSyncContext.Provider value={powerSyncDb}>
      {children}
    </PowerSyncContext.Provider>
  )
}
